import { useEffect, useRef, useState, type ReactNode } from "react";
import { BcSsoClient, type BcIdentityChange } from "@modules/sso";
import { getCurrentUser, type CurrentUser } from "../api/auth";
import { configureBizcentralApiTransport, configureNativeApiTransport } from "../api/client";

const MODULE_ID = "ticketing";
const EXPECTED_AUDIENCE = "module:ticketing";

export type TicketingAuthConfig =
  | { mode: "native" }
  | { mode: "bizcentral"; parentOrigin: string }
  | { mode: "invalid"; reason: "missing_parent_origin" | "invalid_parent_origin" };

function exactOrigin(candidate: string): string | null {
  if (!candidate || candidate === "null" || candidate.includes("*")) return null;
  try {
    const parsed = new URL(candidate);
    return parsed.origin !== "null" && parsed.origin === candidate.replace(/\/+$/, "") ? parsed.origin : null;
  } catch {
    return null;
  }
}

export function loadTicketingAuthConfig(
  env: Record<string, string | boolean | undefined> = import.meta.env,
): TicketingAuthConfig {
  const authSetting = String(env.VITE_REQUIRE_AUTH ?? "").toLowerCase();
  const requireBizcentral = authSetting === "true" || authSetting === "1";
  if (!requireBizcentral) return { mode: "native" };

  const configuredOrigin = String(env.VITE_BC_PLATFORM_BASE ?? "").trim();
  if (!configuredOrigin) return { mode: "invalid", reason: "missing_parent_origin" };
  const parentOrigin = exactOrigin(configuredOrigin);
  if (!parentOrigin) return { mode: "invalid", reason: "invalid_parent_origin" };
  return { mode: "bizcentral", parentOrigin };
}

type AuthenticatedRenderState = {
  generation: number;
  logout: () => void;
  user: CurrentUser;
};

export function BizcentralAuthBootstrap({
  config,
  children,
}: {
  config: Extract<TicketingAuthConfig, { mode: "bizcentral" }>;
  children: (state: AuthenticatedRenderState) => ReactNode;
}) {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [generation, setGeneration] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const clientRef = useRef<BcSsoClient | null>(null);

  useEffect(() => {
    let active = true;
    let requestGeneration = 0;

    const clearIdentity = (reason: string) => {
      requestGeneration += 1;
      setUser(null);
      setError(reason);
    };

    const acceptServerIdentity = async () => {
      const request = ++requestGeneration;
      setError(null);
      try {
        const confirmedUser = await getCurrentUser();
        if (active && request === requestGeneration) setUser(confirmedUser);
      } catch {
        if (active && request === requestGeneration) clearIdentity("identity_verification_failed");
      }
    };

    const handleIdentityChange = (_change: BcIdentityChange) => {
      requestGeneration += 1;
      setUser(null);
      setError(null);
      setGeneration((value) => value + 1);
    };

    const client = new BcSsoClient({
      moduleId: MODULE_ID,
      platformBase: config.parentOrigin,
      allowedParentOrigins: [config.parentOrigin],
      expectedAudience: EXPECTED_AUDIENCE,
      onToken: () => void acceptServerIdentity(),
      onIdentityChange: handleIdentityChange,
      onError: clearIdentity,
    });
    clientRef.current = client;
    configureBizcentralApiTransport(
      () => clientRef.current?.current()?.token ?? null,
      (reason) => {
        clearIdentity(reason);
        clientRef.current?.logout();
      },
    );
    client.start();

    return () => {
      active = false;
      requestGeneration += 1;
      client.destroy();
      if (clientRef.current === client) clientRef.current = null;
      configureNativeApiTransport();
    };
  }, [config.parentOrigin]);

  if (!user) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-zinc-50 px-6 text-zinc-950">
        <p className="text-sm font-medium text-zinc-600" role={error ? "alert" : undefined}>
          {error ? "Your Bizcentral session is unavailable." : "Connecting to Bizcentral..."}
        </p>
      </main>
    );
  }

  return <>{children({ generation, logout: () => clientRef.current?.logout(), user })}</>;
}

export function InvalidBizcentralConfiguration({ reason }: { reason: string }) {
  return (
    <main className="flex min-h-screen items-center justify-center bg-zinc-50 px-6 text-zinc-950">
      <p className="text-sm font-medium text-red-700" role="alert">
        Ticketing cannot start because its trusted Bizcentral parent origin is not configured ({reason}).
      </p>
    </main>
  );
}
